import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  User, CurrencyDollar, Wallet, DownloadSimple, Trash, SignOut, FloppyDisk, ShieldCheck, DeviceMobile,
  UsersThree, ArrowsLeftRight, Plus, Check, Coins, ArrowUp, ArrowDown, ArrowClockwise, Gift, CaretDown, Receipt,
} from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";
import { Card, SectionTitle } from "@/components/riq";
import { CurrencyCombobox } from "@/components/CurrencyCombobox";
import { AutoBudgetToggle } from "@/components/AutoBudgetToggle";

const CAT_KEYS = ["Groceries", "Restaurants", "Fuel", "Household", "Clothing", "Electronics", "Entertainment"];

export default function Settings() {
  const { user, logout, refresh, profiles, switchProfile } = useAuth();
  const navigate = useNavigate();
  const cur = user?.currency || "ZAR";
  const [name, setName] = useState(user?.name || "");
  const [currency, setCurrency] = useState(cur);
  const [accountType, setAccountType] = useState(user?.account_type || "personal");
  const [monthly, setMonthly] = useState("");
  const [catBudgets, setCatBudgets] = useState({});
  const [catKeys, setCatKeys] = useState(CAT_KEYS);
  const [auto, setAuto] = useState({ on: false, months: [], updated: null });
  const applyDef = (def) => {
    setMonthly(def.monthly_budget || "");
    setCatBudgets(def.category_budgets || {});
    setAuto({ on: !!def.auto_budget, months: def.auto_months || [], updated: def.auto_updated_at || null });
  };

  useEffect(() => {
    api.get("/budgets").then((r) => applyDef(r.data.definition || {}));
    api.get("/settings/categories").then((r) => {
      const list = (r.data.categories || []).filter((c) => c !== "Other");
      if (list.length) setCatKeys(list);
    }).catch(() => {});
  }, []);

  const saveProfile = async () => {
    try { await api.patch("/settings/profile", { name, currency, account_type: accountType }); await refresh(); toast.success("Profile updated"); }
    catch { toast.error("Could not update profile"); }
  };

  const saveBudget = async () => {
    try {
      const cb = {};
      Object.entries(catBudgets).forEach(([k, v]) => { if (v) cb[k] = Number(v); });
      await api.post("/budgets", { monthly_budget: monthly ? Number(monthly) : null, category_budgets: cb });
      toast.success("Budget saved");
    } catch { toast.error("Could not save budget"); }
  };

  const toggleAuto = async (enabled) => {
    try {
      const r = await api.post("/budgets/auto", { enabled });
      applyDef(r.data.definition || {});
      const res = r.data.auto_result;
      if (!enabled) toast.success("Automatic budgets off");
      else if (res && res.monthly_budget) toast.success("Budget set from your last 3 months");
      else toast.success("Automatic budgets on. We'll set them once you have a full month of spending");
    } catch { toast.error("Could not update automatic budgets"); }
  };

  const exportData = (format) => {
    window.open(`${API}/settings/export?format=${format}`, "_blank");
  };

  const deleteAccount = async () => {
    if (!window.confirm("Permanently delete your account and all data? This cannot be undone.")) return;
    try { await api.delete("/account"); toast.success("Account deleted"); logout(); navigate("/login"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete account"); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">Settings</h1>

      <InstallCard />

      <Card>
        <div className="flex items-center gap-2 mb-4"><User size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Profile</h3></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name" testId="settings-name" value={name} onChange={setName} />
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Currency</span>
            <CurrencyCombobox testId="settings-currency" value={currency} onChange={setCurrency} className="mt-1 bg-secondary/60 border-transparent" />
          </label>
        </div>
        <div className="mt-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Account type</span>
          <div className="mt-1 grid grid-cols-2 gap-2 max-w-xs">
            {[["personal", "Personal"], ["business", "Business"]].map(([val, lbl]) => (
              <button key={val} type="button" data-testid={`settings-account-${val}`} onClick={() => setAccountType(val)}
                className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${accountType === val ? "border-riq-primary bg-riq-primary/10 text-riq-primary" : "border-riq-border text-neutral-600 hover:border-riq-primary/50"}`}>
                {lbl}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">Business unlocks expense descriptions, trips & expense reports.</p>
        </div>
        <div className="text-xs text-muted-foreground mt-3">{user?.email} · signed in with {user?.auth_provider}</div>
        <button data-testid="save-profile-btn" onClick={saveProfile} className="mt-4 flex items-center gap-2 rounded-full bg-riq-primary text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform"><FloppyDisk size={16} /> Save profile</button>
      </Card>

      <AccountsCard user={user} profiles={profiles} switchProfile={switchProfile} navigate={navigate} />

      <MoolasStatement navigate={navigate} />

      <Card>
        <div className="flex items-center gap-2 mb-4"><Wallet size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Budgets</h3></div>
        <AutoBudgetToggle auto={auto} onToggle={toggleAuto} />
        <Field label="Monthly budget" testId="settings-monthly-budget" type="number" value={monthly} onChange={setMonthly} prefix />
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-4 mb-2">Category budgets</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {catKeys.map((c) => (
            <Field key={c} label={c} testId={`budget-${c}`} type="number" value={catBudgets[c] || ""} onChange={(v) => setCatBudgets((cb) => ({ ...cb, [c]: v }))} prefix />
          ))}
        </div>
        <button data-testid="save-budget-btn" onClick={saveBudget} className="mt-4 flex items-center gap-2 rounded-full bg-riq-primary text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform"><FloppyDisk size={16} /> Save budgets</button>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-4"><DownloadSimple size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Data export</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Download all your receipts, items and budgets.</p>
        <div className="flex gap-3">
          <button data-testid="export-csv-btn" onClick={() => exportData("csv")} className="rounded-full border border-riq-border px-5 py-2.5 text-sm font-medium hover:border-riq-primary">Export CSV</button>
          <button data-testid="export-json-btn" onClick={() => exportData("json")} className="rounded-full border border-riq-border px-5 py-2.5 text-sm font-medium hover:border-riq-primary">Export JSON</button>
        </div>
      </Card>

      {user?.role === "admin" && (
        <Card>
          <div className="flex items-center gap-2 mb-3"><ShieldCheck size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Admin</h3></div>
          <button onClick={() => navigate("/admin")} className="rounded-full bg-riq-primary text-white px-5 py-2.5 text-sm font-medium">Open developer dashboard</button>
        </Card>
      )}

      <Card>
        <h3 className="font-head font-semibold text-destructive mb-3">Danger zone</h3>
        <div className="flex flex-wrap gap-3">
          <button data-testid="settings-logout-btn" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-2 rounded-full border border-riq-border px-5 py-2.5 text-sm font-medium"><SignOut size={16} /> Log out</button>
          <button data-testid="delete-account-btn" onClick={deleteAccount} className="flex items-center gap-2 rounded-full border border-destructive text-destructive px-5 py-2.5 text-sm font-medium"><Trash size={16} /> Delete account</button>
        </div>
      </Card>
    </div>
  );
}

const REDEMPTION_STATUS = {
  pending: { label: "Pending", cls: "bg-riq-gold/25 text-[#8a5a00]" },
  fulfilled: { label: "Fulfilled", cls: "bg-[#E4F7ED] text-[#22B573]" },
  rejected: { label: "Rejected", cls: "bg-[#FFE3E1] text-[#b3261e]" },
};

function redemptionState(r) {
  if (r.status === "rejected") return REDEMPTION_STATUS.rejected;
  if ((r.fulfilment_status || "").toLowerCase() === "fulfilled") return REDEMPTION_STATUS.fulfilled;
  return REDEMPTION_STATUS.pending;
}

const TXN_META = {
  earn: { Icon: ArrowUp, cls: "bg-[#E4F7ED] text-[#22B573]" },
  redeem: { Icon: Gift, cls: "bg-riq-primary/10 text-riq-primary" },
  reversal: { Icon: ArrowDown, cls: "bg-[#FFE3E1] text-[#b3261e]" },
  adjust: { Icon: ArrowClockwise, cls: "bg-secondary text-neutral-600" },
};

function MoolasStatement({ navigate }) {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/rewards/statement").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === false) return null;

  const t = data?.totals || {};
  const txns = data?.transactions || [];
  const redemptions = data?.redemptions || [];
  const shown = showAll ? txns : txns.slice(0, 6);

  const Stat = ({ label, value, color, testId }) => (
    <div className="px-3 py-3 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div data-testid={testId} className="font-head text-xl font-bold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );

  return (
    <Card data-testid="settings-moolas-statement" className="!p-0 overflow-hidden">
      <div className="flex items-center justify-between px-5 pt-5">
        <div className="flex items-center gap-2"><Coins size={20} weight="duotone" className="text-riq-gold" /><h3 className="font-head font-semibold text-riq-primary">Moolas statement</h3></div>
        <button data-testid="statement-rewards-link" onClick={() => navigate("/rewards")} className="text-sm text-riq-primary font-medium">Rewards →</button>
      </div>

      {data === null ? (
        <div className="px-5 py-8 text-sm text-muted-foreground">Loading your statement…</div>
      ) : (
        <>
          {/* Balance + totals */}
          <div className="mx-5 mt-4 rounded-2xl bg-riq-primary text-white px-5 py-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60">Current balance</div>
            <div data-testid="statement-balance" className="font-head text-3xl font-bold mt-1">{data.balance} <span className="text-lg font-semibold text-riq-gold">Moolas</span></div>
          </div>
          <div className="grid grid-cols-3 divide-x divide-riq-border border-y border-riq-border mt-4">
            <Stat label="Earned" value={`+${t.earned || 0}`} color="#22B573" testId="statement-earned" />
            <Stat label="Redeemed" value={`-${t.redeemed || 0}`} color="#6E3FFC" testId="statement-redeemed" />
            <Stat label="Reversed" value={`-${t.reversed || 0}`} color="#FF6B6B" testId="statement-reversed" />
          </div>

          {/* Redemptions with status */}
          {redemptions.length > 0 && (
            <div className="px-5 pt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Redemptions</div>
              <div className="space-y-2">
                {redemptions.map((r) => {
                  const st = redemptionState(r);
                  return (
                    <div key={r.id} data-testid={`statement-redemption-${r.id}`} className="flex items-center gap-3 rounded-xl border border-riq-border px-3 py-2.5">
                      <span className="grid h-9 w-9 place-items-center rounded-xl bg-riq-primary/10 text-riq-primary shrink-0"><Gift size={17} weight="duotone" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{r.title}</div>
                        <div className="text-xs text-muted-foreground">{shortDate(r.at)} · {r.cost} Moolas</div>
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${st.cls}`}>{st.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Earnings / activity ledger */}
          <div className="px-5 py-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Activity</div>
            {txns.length === 0 ? (
              <div className="text-sm text-muted-foreground">No Moolas activity yet. Scan a receipt to start earning.</div>
            ) : (
              <div className="divide-y divide-riq-border">
                {shown.map((x, i) => {
                  const meta = TXN_META[x.type] || TXN_META.adjust;
                  const pos = (x.amount || 0) > 0;
                  return (
                    <div key={i} data-testid={`statement-txn-${i}`} className="flex items-center gap-3 py-2.5">
                      <span className={`grid h-8 w-8 place-items-center rounded-lg shrink-0 ${meta.cls}`}><meta.Icon size={15} weight="bold" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{x.reason || (x.type === "earn" ? "Earned Moolas" : "Moolas adjustment")}</div>
                        <div className="text-xs text-muted-foreground">{shortDate(x.at)}</div>
                      </div>
                      <div className={`font-head font-bold tabular-nums shrink-0 ${pos ? "text-[#22B573]" : "text-[#b3261e]"}`}>{pos ? "+" : ""}{x.amount}</div>
                    </div>
                  );
                })}
                {txns.length > 6 && (
                  <button data-testid="statement-toggle" onClick={() => setShowAll((s) => !s)} className="w-full pt-3 text-sm text-riq-primary font-medium flex items-center justify-center gap-1">
                    {showAll ? "Show less" : `Show all ${txns.length}`} <CaretDown size={14} className={showAll ? "rotate-180" : ""} />
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function AccountsCard({ user, profiles, switchProfile, navigate }) {
  const others = (profiles || []).filter((p) => p.user_id !== user?.user_id);

  const doSwitch = async (p) => {
    try {
      toast.loading(`Switching to ${p.name || p.email}…`, { id: "switch" });
      await switchProfile(p); // reloads on success
    } catch {
      toast.error("That session expired — please sign in to that account again.", { id: "switch" });
      navigate("/login?add=1");
    }
  };

  return (
    <Card data-testid="settings-accounts">
      <div className="flex items-center gap-2 mb-1"><UsersThree size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Accounts</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Create and switch between separate accounts on this device. Each account keeps its own receipts, budgets and Moolas.</p>

      <div className="space-y-2">
        <div data-testid="account-current" className="flex items-center gap-3 rounded-xl border border-riq-primary/30 bg-riq-primary/5 px-3 py-2.5">
          <span className="w-9 h-9 rounded-xl bg-riq-primary text-white flex items-center justify-center text-sm font-bold shrink-0">
            {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{user?.name || user?.email?.split("@")[0]}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <span className="text-[11px] font-semibold text-riq-primary bg-white rounded-full px-2.5 py-1 shrink-0 flex items-center gap-1"><Check size={12} weight="bold" /> Current</span>
        </div>

        {others.map((p) => (
          <div key={p.user_id} data-testid={`account-linked-${p.user_id}`} className="flex items-center gap-3 rounded-xl border border-riq-border px-3 py-2.5">
            <span className="w-9 h-9 rounded-xl bg-secondary text-riq-primary flex items-center justify-center text-sm font-bold shrink-0">
              {(p.name || p.email || "?").trim().charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{p.name || p.email.split("@")[0]}</div>
              <div className="text-xs text-muted-foreground truncate">{p.email}</div>
            </div>
            <button data-testid={`account-switch-${p.user_id}`} onClick={() => doSwitch(p)}
              className="text-xs font-semibold text-riq-primary border border-riq-border rounded-full px-3 py-1.5 hover:bg-secondary transition-colors shrink-0 flex items-center gap-1.5">
              <ArrowsLeftRight size={13} weight="bold" /> Switch
            </button>
          </div>
        ))}
      </div>

      <button data-testid="settings-add-account-btn" onClick={() => navigate("/login?add=1")}
        className="mt-4 flex items-center gap-2 rounded-full border border-riq-primary text-riq-primary px-5 py-2.5 text-sm font-medium hover:bg-riq-primary/5 transition-colors">
        <Plus size={16} weight="bold" /> Add another account
      </button>
    </Card>
  );
}

function InstallCard() {
  const [installable, setInstallable] = useState(!!window.__sliplyInstall);
  const [installed, setInstalled] = useState(window.matchMedia?.("(display-mode: standalone)").matches);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    const onReady = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); };
    window.addEventListener("sliply-installable", onReady);
    window.addEventListener("sliply-installed", onInstalled);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("sliply-installable", onReady); window.removeEventListener("sliply-installed", onInstalled); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    const e = window.__sliplyInstall;
    if (!e) return;
    e.prompt();
    try { await e.userChoice; } catch {}
    window.__sliplyInstall = null;
    setInstallable(false);
  };

  if (installed) {
    return (
      <Card>
        <div className="flex items-center gap-3"><DeviceMobile size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Installed</h3></div>
        <p className="text-sm text-muted-foreground mt-2">Sliply is running as an installed app. Enjoy the full-screen experience!</p>
      </Card>
    );
  }

  return (
    <Card className="bg-riq-primary text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07] riq-noise" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-2"><DeviceMobile size={22} weight="duotone" className="text-riq-gold" /><h3 className="font-head text-lg font-semibold">Install Sliply</h3></div>
        <p className="text-sm text-white/80 leading-relaxed">Add Sliply to your home screen for a fast, full-screen app experience — scan receipts in one tap.</p>
        {installable ? (
          <button data-testid="install-app-btn" onClick={install} className="mt-4 rounded-full bg-white text-riq-primary px-5 py-2.5 text-sm font-semibold hover:-translate-y-0.5 transition-transform">Install app</button>
        ) : isIOS ? (
          <p className="mt-3 text-sm text-white/90">On iPhone/iPad: tap the <b>Share</b> icon, then <b>“Add to Home Screen”</b>.</p>
        ) : (
          <p className="mt-3 text-sm text-white/90">Open your browser menu and choose <b>“Install app”</b> / <b>“Add to Home screen”</b>.</p>
        )}
      </div>
    </Card>
  );
}

function Field({ label, value, onChange, type = "text", prefix, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="relative mt-1">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R</span>}
        <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className={`w-full bg-secondary/60 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-riq-primary/20 ${prefix ? "pl-7" : ""}`} />
      </div>
    </label>
  );
}
